import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../config/axiosApi/axiosApi";
import APIs from "../Constants/apis";
import useDebouncedValue from "./useDebouncedValue";

const DEFAULT_PAGE_SIZE = 40;

const getProductId = (product) =>
  product?.menuItem_ID ?? product?.MenuItem_ID ?? product?.id;

const normalizeExcludeIds = (excludeIds) => {
  if (!Array.isArray(excludeIds) || excludeIds.length === 0) return new Set();
  return new Set(
    excludeIds
      .map((id) => {
        if (id == null) return null;
        if (typeof id === "object") return String(getProductId(id) ?? "");
        return String(id);
      })
      .filter(Boolean)
  );
};

/**
 * Infinite-scroll + server-search loader for short product picker lists.
 */
export default function usePagedShortProducts({
  enabled = false,
  searchTerm = "",
  pageSize = DEFAULT_PAGE_SIZE,
  excludeIds = [],
  compID: compIDProp,
} = {}) {
  const authCompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const CompID = compIDProp || authCompID;
  const debouncedSearch = useDebouncedValue(searchTerm, 350);
  const excludeSet = useMemo(
    () => normalizeExcludeIds(excludeIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(excludeIds)]
  );

  const [products, setProducts] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const hasMore = products.length < totalRecords;

  const fetchPage = useCallback(
    async (page, { append } = { append: false }) => {
      if (!CompID || !enabled) return;

      const requestId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.get(APIs.GET_ALL_SHORTED_PRODUCTS, {
          params: {
            CompID,
            PageNumber: page,
            PageSize: pageSize,
            Search: debouncedSearch?.trim() || undefined,
          },
        });

        if (requestId !== requestIdRef.current) return;

        if (response.data?.status === false) {
          throw new Error(response.data?.message || "Failed to fetch products");
        }

        const raw = response.data?.data || [];
        const list = Array.isArray(raw) ? raw : [];
        const filtered = excludeSet.size
          ? list.filter((p) => !excludeSet.has(String(getProductId(p) ?? "")))
          : list;

        const total =
          Number(response.data?.totalRecords ?? response.data?.TotalRecords) ||
          0;

        setTotalRecords(total);
        setPageNumber(page);
        setProducts((prev) => (append ? [...prev, ...filtered] : filtered));
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(err);
        if (!append) {
          setProducts([]);
          setTotalRecords(0);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [CompID, enabled, pageSize, debouncedSearch, excludeSet]
  );

  // Reset and load first page when modal opens / search changes
  useEffect(() => {
    if (!enabled || !CompID) {
      setProducts([]);
      setPageNumber(1);
      setTotalRecords(0);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    fetchPage(1, { append: false });
  }, [enabled, CompID, debouncedSearch, fetchPage]);

  const loadMore = useCallback(() => {
    if (!enabled || loading || loadingMore || !hasMore) return;
    fetchPage(pageNumber + 1, { append: true });
  }, [enabled, loading, loadingMore, hasMore, fetchPage, pageNumber]);

  const handleScroll = useCallback(
    (event) => {
      const el = event?.currentTarget;
      if (!el) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 80) loadMore();
    },
    [loadMore]
  );

  return {
    products,
    loading,
    loadingMore,
    hasMore,
    error,
    totalRecords,
    loadMore,
    handleScroll,
    refresh: () => fetchPage(1, { append: false }),
  };
}

/**
 * Hydrate short product details for saved IDs (chips / labels outside the modal).
 */
export async function fetchMenuItemsByIds(compID, ids = []) {
  const uniqueIds = [
    ...new Set(
      (ids || [])
        .map((id) => {
          if (id == null) return null;
          if (typeof id === "object") return getProductId(id);
          return id;
        })
        .filter((id) => id != null && String(id).trim() !== "" && Number(id) > 0)
        .map((id) => String(id))
    ),
  ];

  if (!compID || uniqueIds.length === 0) return [];

  const response = await axiosInstance.get(APIs.GET_MENU_ITEMS_BY_IDS, {
    params: { CompID: compID, Ids: uniqueIds.join(",") },
  });

  if (response.data?.status === false) {
    throw new Error(response.data?.message || "Failed to fetch products by id");
  }

  const data = response.data?.data || [];
  return Array.isArray(data) ? data : [];
}
